import React, { useState, useEffect, useContext } from 'react'
import { Link, useLocation } from 'react-router-dom'
import MuiLink from '@mui/material/Link'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Pagination from '@mui/material/Pagination'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import Typography from '@mui/material/Typography'
import { getAllBrandsRequest } from 'api/brand.api'
import { ToastContext } from 'context/toastContext'
import { categoryList } from 'constants/categories'
import useDebounce from 'hooks/useDebounce'
import useStyles from 'components/orders/style'
import Spinner from 'components/Loader'
import { toast_actions, toast_types } from 'components/shared/toast/utils/toast'
import ProvidersCard from 'views/ProvidersCard'
import { ReactComponent as SearchIcon } from 'assets/images/search.svg'
import {
  ProviderWrapper,
  BreadcrumbWrapper,
  ProvidersListingWrapper,
  HeaderSection,
  MainHeading,
  CardContainer,
  PaginationWrapper,
  SearchWrapper,
  SearchContent,
} from 'styles/ProvidersList'

const Providers = () => {
  const classes = useStyles()
  const dispatch = useContext(ToastContext)
  const [providers, setProviders] = useState([])
  const [paginationModel, setPaginationModel] = useState({
    page: 1,
    pageSize: 18,
  })
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [searchData, setSearchData] = useState('')
  const debouncedSearch = useDebounce(searchData, 500)
  const locationData = useLocation()

  const useQuery = () => {
    const { search } = locationData
    return React.useMemo(() => new URLSearchParams(search), [search])
  }

  let query = useQuery()

  useEffect(() => {
    const categoryName = query.get('c')

    // Trigger the API call when categoryName, debouncedSearch, or paginationModel changes
    if (categoryName || debouncedSearch || paginationModel.page) {
      getAllBrands(categoryName, debouncedSearch)
    }
  }, [locationData, paginationModel, debouncedSearch])

  const getAllBrands = async (categoryName, searchName) => {
    setIsLoading(true)
    try {
      const findCategory = categoryList.find((item) => item.routeName === categoryName)
      const domain = findCategory?.domain
      let paginationData = { ...paginationModel }

      let data
      if (searchName && searchName.length) {
        // If searchName exists, search with page 1 and pageSize 18
        data = await getAllBrandsRequest({ page: 1, pageSize: 18 }, domain, searchName)
      } else {
        // Fetch brands based on pagination
        data = await getAllBrandsRequest(paginationData, domain, '')
      }

      setProviders(data?.data)
      setTotalCount(data?.count)
    } catch (err) {
      dispatch({
        type: toast_actions.ADD_TOAST,
        payload: {
          id: Math.floor(Math.random() * 100),
          type: toast_types.error,
          message: err?.response?.data?.error?.message || err.message,
        },
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleProviderSearch = () => {
    // Reset page to 1 when a new search is triggered
    setPaginationModel({ ...paginationModel, page: 1 })
    getAllBrands(query.get('c'), searchData)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleProviderSearch()
    }
  }

  const handlePaginationChange = (event, page) => {
    event
    setPaginationModel({ ...paginationModel, page })
    window.scrollTo({ top: 0, behavior: 'smooth' }) // Scroll to top when pagination changes
  }

  return (
    <ProviderWrapper>
      <BreadcrumbWrapper>
        <Breadcrumbs aria-label="breadcrumb">
          <MuiLink component={Link} underline="hover" color="inherit" to="/">
            <Typography color="#3A3A88">Home</Typography>
          </MuiLink>
          <Typography>Providers</Typography>
        </Breadcrumbs>
      </BreadcrumbWrapper>
      <ProvidersListingWrapper>
        <HeaderSection>
          <MainHeading>Providers</MainHeading>
          <div>
            <SearchWrapper>
              <SearchContent>
                <IconButton
                  type="button"
                  aria-label="search"
                  onClick={(event) => {
                    event.stopPropagation()
                    handleProviderSearch()
                  }}
                >
                  <SearchIcon />
                </IconButton>
                <InputBase
                  fullWidth
                  className={classes.inputBase}
                  placeholder="Search..."
                  inputProps={{ 'aria-label': 'Search...' }}
                  name="search"
                  value={searchData || ''}
                  onKeyDown={handleKeyDown}
                  onChange={(e) => setSearchData(e.target.value)}
                />
              </SearchContent>
            </SearchWrapper>
          </div>
        </HeaderSection>
        <CardContainer>{isLoading ? <Spinner /> : <ProvidersCard providers={providers} />}</CardContainer>
      </ProvidersListingWrapper>

      {providers?.length > 0 && totalCount > paginationModel.pageSize && (
        <PaginationWrapper>
          <Pagination
            className={classes.pagination}
            count={Math.ceil(totalCount / paginationModel.pageSize)}
            shape="rounded"
            color="primary"
            page={paginationModel.page}
            onChange={handlePaginationChange}
          />
        </PaginationWrapper>
      )}
    </ProviderWrapper>
  )
}

export default Providers
